import type en from './en.json';

export type Locale = 'en' | 'ru' | 'es' | 'pt' | 'tr' | 'zh';
export const AVAILABLE_LOCALES: readonly Locale[] = ['en', 'ru', 'es', 'pt', 'tr', 'zh'] as const;

/**
 * Locale prefix alternation for path regexes. Derived, never hand-written: the
 * backend learned that lesson when the same list sat in thirteen files and
 * roughly thirty-five inline regexes (util/locales.ts, 2026-07-29).
 */
export const PREFIX_ALTERNATION = AVAILABLE_LOCALES.filter((l) => l !== 'en').join('|');
export const DEFAULT_LOCALE: Locale = 'en';

/**
 * BCP-47 tag for `Intl` — dates, numbers, currency.
 *
 * Three files kept their own copy of this map and all three stopped at `tr`,
 * so a Chinese reader got English-formatted dates on the country pages, in the
 * eSIM status modal, and anywhere else the fallback fired. `Record<Locale,…>`
 * means the compiler now refuses the next incomplete copy.
 */
export const INTL_LOCALE: Record<Locale, string> = {
  en: 'en-US',
  ru: 'ru-RU',
  es: 'es-ES',
  pt: 'pt-BR',
  tr: 'tr-TR',
  zh: 'zh-CN',
};

/** `og:locale` form, underscore-separated. Mirrors the server's OG_LOCALE. */
export const OG_LOCALE: Record<Locale, string> = {
  en: 'en_US',
  ru: 'ru_RU',
  es: 'es_ES',
  pt: 'pt_BR',
  tr: 'tr_TR',
  zh: 'zh_CN',
};

export type Bundle = typeof en;

export type Path<T, Prefix extends string = ''> = T extends string
  ? Prefix
  : T extends object
    ? {
        [K in keyof T & string]: Path<
          T[K],
          Prefix extends '' ? K : `${Prefix}.${K}`
        >;
      }[keyof T & string]
    : never;

export type TranslationKey = Path<Bundle>;
